import React, {useState, useEffect, FC} from 'react';
import {
	messageBoxSuccess,
	ReactableWithClientSidePagination,
	ContentHeader,
	messageBoxError
} from '@deviceinsight/ng-ui-components';
import {SpinnerContainer, Button, withConfirm, ConfirmProps, Bubble} from '@deviceinsight/ng-ui-basic-components';
import {CustomDatapoint} from '../../modules/customDatapoints/types';
import hasAuthority from '../common/hasAuthority';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {deleteCustomDatapoint, listCustomDatapoints} from '../../api/customDatapointsApi';
import {Asset} from '@deviceinsight/ng-ui-api-client';
import {WRITE_CUSTOM_DATAPOINT} from '../../modules/common/consts';
import ValueColumnInputComponent from './ValueColumnInputComponent';
import CreateCustomDatapointModal from './CreateCustomDatapointModal';

import './CustomDatapointsPage.less';

type Props = {
	asset: Asset;
} & ConfirmProps;

const CustomDatapointsPage: FC<Props> = ({confirm, asset}) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [customDpLimitReached, setCustomDpLimitReached] = useState<boolean>(false);
	const [createModalIsOpen, setCreateModalIsOpen] = useState<boolean>(false);
	const [customDatapoints, setCustomDatapoints] = useState<CustomDatapoint[]>([]);

	const canWriteCustomDp = hasAuthority(WRITE_CUSTOM_DATAPOINT);

	const CUSTOM_DP_HEADER = {
		id: 'customDatapoints.title',
		defaultValue: 'Custom Datapoints'
	};
	const CREATE_CUSTOM_DP_BUTTON_TOOLTIP = (
		<Trans i18nKey="customDatapoints.createButtonTooltip">Create Custom Datapoint</Trans>
	);
	const DELETE_CUSTOM_DP_CONFIRMATION_TITLE = {
		id: 'customDatapoints.deleteModal.deleteCustomDpConfirmTitle',
		defaultValue: 'Delete Custom Datapoint'
	};
	const DELETE_CUSTOM_DP_CONFIRMATION_BODY = {
		id: 'customDatapoints.deleteModal.deleteCustomDpConfirmBody',
		defaultValue: 'Do you really want to delete the Custom Datapoint?'
	};

	useEffect(() => {
		loadCustomDps();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [asset]);

	const loadCustomDps = (showLoader = true) => {
		setLoading(showLoader);
		listCustomDatapoints(asset.urn)
			.then((dps) => {
				setCustomDatapoints(dps);
				if (dps.length === 10) {
					setCustomDpLimitReached(true);
				}
			})
			.catch((error) => {
				console.error('Custom Datapoints could not be loaded: ', error);
			})
			.finally(() => setLoading(false));
	};

	const deleteCustomDp = (dpId: string) => {
		confirm(DELETE_CUSTOM_DP_CONFIRMATION_TITLE, DELETE_CUSTOM_DP_CONFIRMATION_BODY).then(() => {
			setLoading(true);
			deleteCustomDatapoint(asset.urn, dpId)
				.catch(() =>
					messageBoxError(
						<Trans i18nKey="customDatapoints.deleteCustomDp.customDpDeletionError">
							Custom Datapoint was not deleted!
						</Trans>
					)
				)
				.then(() => {
					messageBoxSuccess(
						<Trans i18nKey="customDatapoints.deleteCustomDp.customDpDeletionSuccess">
							Custom Datapoint was deleted
						</Trans>
					);
					loadCustomDps();
				})
				.finally(() => setLoading(false));
		});
	};

	const columnDefs = [
		{
			name: 'name',
			label: {
				id: 'customDatapoints.columns.name',
				defaultValue: 'Name'
			},
			width: '37.5%',
			sortable: true,
			render: (element: CustomDatapoint) => {
				return <span data-testid={`name-${element.name}`}>{element.name}</span>;
			}
		},
		{
			name: 'value',
			label: {
				id: 'customDatapoints.columns.value',
				defaultValue: 'Value'
			},
			width: '37.5%',
			sortable: true,
			render: (element: CustomDatapoint) => {
				return (
					<ValueColumnInputComponent
						refresh={() => loadCustomDps(false)}
						disabled={!canWriteCustomDp}
						asset={asset}
						dp={element}></ValueColumnInputComponent>
				);
			}
		},
		{
			name: 'delete',
			label: '',
			width: '25%',
			sortable: false,
			render: (element: CustomDatapoint) => {
				return (
					<span data-testid="deleteCustomDp">
						{canWriteCustomDp && (
							<Button
								danger
								data-testid={`deleteButton-${element.name}`}
								type="submit"
								disabled={false}
								onClick={() => deleteCustomDp(element.id)}>
								<Trans i18nKey="customDatapoints.columns.delete">Delete</Trans>
							</Button>
						)}
					</span>
				);
			}
		}
	];

	return (
		<>
			<div style={{marginTop: 30}}>
				{createModalIsOpen && (
					<CreateCustomDatapointModal
						asset={asset}
						setLoading={setLoading}
						setCreateModalIsOpen={setCreateModalIsOpen}
						loadCustomDps={loadCustomDps}></CreateCustomDatapointModal>
				)}
				{canWriteCustomDp && !customDpLimitReached && (
					<ContentHeader title={CUSTOM_DP_HEADER}>
						<div className="di header-toolbar">
							<Bubble direction="bottom" content={CREATE_CUSTOM_DP_BUTTON_TOOLTIP}>
								<Button
									className="tool add"
									style={{border: 0}}
									onClick={() => {
										setCreateModalIsOpen(true);
									}}
									data-testid="addDpButton"
								/>
							</Bubble>
						</div>
					</ContentHeader>
				)}
				<div className="custom-datapoints">
					<SpinnerContainer show={loading}>
						<div>
							<ReactableWithClientSidePagination
								columnDefs={columnDefs}
								isLoading={loading}
								items={customDatapoints}
								uniqueItemKey={(customDatapoint: CustomDatapoint) => customDatapoint.id}
								paginationSettings={{page: 0, size: 999, sort: {property: 'name', direction: 'ASC'}}}
							/>
						</div>
					</SpinnerContainer>
				</div>
			</div>
		</>
	);
};

export default withConfirm(CustomDatapointsPage);
